import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ChevronRight, Edit3, Globe, Search } from 'lucide-react';
import api from '../api';
import { Input } from '../../components/ui/input';

export default function PagesList() {
  const [pages, setPages] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/pages');
        setPages(data);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = pages.filter((p) =>
    p.title.toLowerCase().includes(q.toLowerCase()) || p.slug.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[11px]">
          <FileText className="h-3 w-3" /> Pages
        </div>
        <h1 className="font-serif-display text-[36px] lg:text-[42px] font-bold mt-2">All pages</h1>
        <p className="text-white/55 text-[14px] mt-1">Click a page to edit its content with a live preview.</p>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
        <Input
          value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pages…"
          className="h-11 pl-10 rounded-xl bg-[#16131a] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
        />
      </div>

      <div className="rounded-2xl bg-[#16131a] border border-white/[0.06] overflow-hidden">
        {loading && <div className="p-8 text-center text-white/50 text-[14px]">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-8 text-center text-white/50 text-[14px]">No pages match your search.</div>
        )}
        {filtered.map((p, i) => (
          <Link
            key={p.slug} to={`/admin/pages/${p.slug}`}
            className={`group flex items-center justify-between gap-5 px-6 py-5 hover:bg-white/[0.04] transition-colors ${i < filtered.length - 1 ? 'border-b border-white/[0.06]' : ''}`}
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="h-11 w-11 rounded-xl bg-[#E5614C]/15 text-[#E5614C] flex items-center justify-center group-hover:bg-[#E5614C] group-hover:text-white transition-colors">
                <FileText className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="font-serif-display font-bold text-[18px] truncate">{p.title}</div>
                <div className="text-[12px] text-white/45 truncate flex items-center gap-1.5">
                  <Globe className="h-3 w-3" /> {p.route} · {p.sections?.length || 0} sections · {totalFields(p)} editable fields
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-white/45 group-hover:text-white shrink-0">
              <span className="text-[12px] font-semibold">Edit</span>
              <Edit3 className="h-4 w-4" />
              <ChevronRight className="h-4 w-4" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function totalFields(p) {
  return (p.sections || []).reduce((acc, s) => acc + (s.fields?.length || 0), 0);
}
